import React, { useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Box, Checkbox, HStack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { useInsuredActiveRecordsData } from '../../../../services/insured-service';
import { useCompanyActiveRecordsSearch } from '../../../../services/record-service';
import { getInfiniteListData } from '../../../../services/util';
import colors from '../../../../styles/theme/foundation/colors';
import PulseLoader from '../../../PulseLoader';
import SearchInputGroup from '../../../SearchInputGroup';

import RecordSearchRow from './components/RecordSearchRow';

export const SEARCH_DEBOUNCE_MS = 300;
export const MAX_SELECTED_RECORDS = 50;
const SCROLL_TARGET_ID = 'clone-record-search-scroll';

/**
 * The insured records endpoint returns Eloquent records; the search index returns documents. Both
 * are reshaped to what RecordSearchRow reads.
 */
export const insuredRecordToRow = (record, insuredName) => ({
  insured: { id: record.insuredId, companyName: insuredName },
  job: { id: record.jobId ?? record.job?.id, name: record.job?.jobName, number: record.job?.jobNumber },
  insuranceRequirement: { id: record.id, contractNumber: record.contractNumber }
});

const plural = count => (count === 1 ? '' : 's');

const RecordSearchList = ({ companyId, insuredId, insuredName, excludeRecordId, recordObject, setRecordObject }) => {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => setSearchTerm(searchInput.trim()), SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const insuredQuery = useInsuredActiveRecordsData({ insuredId, pageSize: 100, options: { enabled: !!insuredId } });
  const insuredRecords = useMemo(() => getInfiniteListData(insuredQuery.data)
    .filter(record => record.id !== parseInt(excludeRecordId, 10))
    .map(record => insuredRecordToRow(record, insuredName)), [insuredQuery.data, excludeRecordId, insuredName]);

  const searchQuery = useCompanyActiveRecordsSearch({ companyId, searchTerm, excludeRecordId, excludeInsuredId: insuredId });
  const { data, isLoading, isFetching, hasNextPage, fetchNextPage } = searchQuery;

  const searchRecords = useMemo(() => getInfiniteListData(data), [data]);
  const total = data?.pages?.[0]?.total ?? 0;

  const selectedCount = useMemo(() => Object.values(recordObject).filter(Boolean).length, [recordObject]);
  const limitReached = selectedCount >= MAX_SELECTED_RECORDS;

  const handleToggle = (recordId, checked) => {
    if (checked && limitReached) {
      return;
    }

    setRecordObject({ ...recordObject, [recordId]: checked });
  };

  const insuredRecordIds = useMemo(() => insuredRecords.map(r => r.insuranceRequirement.id), [insuredRecords]);
  const selectedInsuredCount = insuredRecordIds.filter(id => !!recordObject[id]).length;
  const allInsuredSelected = insuredRecordIds.length > 0 && selectedInsuredCount === insuredRecordIds.length;

  // Selects the insured's records in list order until the cap is hit; unchecking clears only them.
  const handleToggleAllInsured = checked => {
    const next = { ...recordObject };
    let remaining = MAX_SELECTED_RECORDS - selectedCount;

    insuredRecordIds.forEach(id => {
      if (!checked) {
        next[id] = false;

        return;
      }

      if (next[id] || remaining <= 0) {
        return;
      }

      next[id] = true;
      remaining -= 1;
    });

    setRecordObject(next);
  };

  const renderRow = record => (
    <RecordSearchRow
      isChecked={!!recordObject[record.insuranceRequirement?.id]}
      isDisabled={limitReached && !recordObject[record.insuranceRequirement?.id]}
      key={record.insuranceRequirement?.id}
      record={record}
      onToggle={handleToggle}
    />
  );

  return (
    <Box className="RecordSearchList" display="flex" flexDirection="column" h="100%" minH={0}>
      <HStack justifyContent="space-between" mb={2}>
        <Text textStyle="displayXxs">Select Record(s) to Receive Copy</Text>
        <Text color={limitReached ? colors.primaryScale['600'] : colors.greyScale['700']} textStyle="bodyXs500">
          {`${selectedCount} of ${MAX_SELECTED_RECORDS} selected`}
        </Text>
      </HStack>

      <Box flex="1 1 auto" id={SCROLL_TARGET_ID} minH={0} overflowY="auto" pr={1}>
        <Box data-cy="clone-insured-records" mb={6}>
          <HStack justifyContent="space-between" mb={2}>
            <Text textStyle="bodyMd500">{`${insuredName || 'This insured'}'s other records`}</Text>
            <HStack spacing={4}>
              <Text color={colors.greyScale['700']} textStyle="bodyXs">
                {insuredQuery.isLoading ? 'Loading...' : `${insuredRecords.length} record${plural(insuredRecords.length)}`}
              </Text>
              <Checkbox
                data-cy="clone-insured-records-select-all"
                isChecked={allInsuredSelected}
                isDisabled={!insuredRecordIds.length || (!allInsuredSelected && limitReached)}
                isIndeterminate={selectedInsuredCount > 0 && !allInsuredSelected}
                onChange={e => handleToggleAllInsured(e.target.checked)}
              >
                <Text textStyle="bodyXs500">Select All</Text>
              </Checkbox>
            </HStack>
          </HStack>

          {insuredQuery.isLoading && <PulseLoader />}

          {!insuredQuery.isLoading && !insuredRecords.length && (
            <Text color={colors.greyScale['700']} my={4} textAlign="center" textStyle="bodySm">
              This insured has no other active records.
            </Text>
          )}

          {insuredRecords.map(renderRow)}

          {insuredQuery.hasNextPage && (
            <Text
              color={colors.primaryScale['600']}
              cursor="pointer"
              textAlign="center"
              textStyle="bodySm500"
              onClick={() => insuredQuery.fetchNextPage()}
            >
              Load more
            </Text>
          )}
        </Box>

        <Box data-cy="clone-other-insured-records">
          <Text mb={2} textStyle="bodyMd500">Records on other insureds</Text>

          <SearchInputGroup
            cypressId="clone-record-search"
            mb={2}
            placeHolder="Search by insured, project name, project number or record number"
            searchTerm={searchInput}
            variant="secondary"
            onChange={e => setSearchInput(e.target.value)}
          />

          <Text color={colors.greyScale['700']} mb={2} textStyle="bodyXs">
            {isLoading ? 'Loading records...' : `${total} record${plural(total)} found`}
          </Text>

          {isLoading && <PulseLoader />}

          {!isLoading && !searchRecords.length && (
            <Text my={4} textAlign="center">
              {searchTerm ? 'No records match your search.' : 'None available'}
            </Text>
          )}

          {!isLoading && searchRecords.length > 0 && (
            <InfiniteScroll
              dataLength={searchRecords.length}
              hasMore={!!hasNextPage}
              loader={<PulseLoader />}
              next={fetchNextPage}
              scrollableTarget={SCROLL_TARGET_ID}
              scrollThreshold={0.75}
              style={{ overflow: 'visible', opacity: isFetching && !hasNextPage ? 0.7 : 1 }}
            >
              {searchRecords.map(renderRow)}
            </InfiniteScroll>
          )}
        </Box>
      </Box>
    </Box>
  );
};

RecordSearchList.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  insuredId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  insuredName: PropTypes.string,
  excludeRecordId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  recordObject: PropTypes.objectOf(PropTypes.bool).isRequired,
  setRecordObject: PropTypes.func.isRequired
};

export default RecordSearchList;
